import { useEffect, useRef, useState } from 'react'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient } from '../services/api'
import { ui } from '../styles/ui'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import PipelineSteps from '../components/PipelineSteps'
import StatusBadge from '../components/StatusBadge'
import { formatDateTime } from '../utils/date'
import { formatCurrency } from '../utils/currency'
import { formatConfidence, formatEmptyValue } from '../utils/displayValue'
import usePolling from '../hooks/usePolling'
import { useTranslation } from '../contexts/TranslationContext'
import { useCurrencyCatalog } from '../contexts/CurrencyCatalogContext'
import receiptImageAssetsService from '../services/receiptImageAssetsService'
import SearchableCurrencySelect from '../components/ui/SearchableCurrencySelect'

// Tabs shown in the detail view
const TABS = [
  { key: 'parsed', label: 'Parsed' },
  { key: 'raw', label: 'Raw' },
  { key: 'debug', label: 'Debug' },
]

function normalizeSourceType(receipt) {
  return String(receipt?.source_type ?? '').trim().toLowerCase()
}

function resolveReprocessStrategy(receipt) {
  const sourceType = normalizeSourceType(receipt)
  // WHY: callers should be explicit about the retry path so QR/image reprocess
  // always goes through the filesystem cache instead of depending on server defaults.
  if (sourceType === 'speech') return 'default'
  return 'cache'
}

function parseConfidenceCandidate(value) {
  if (value == null || value === '') return null
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return null
  if (parsed <= 1) return Math.max(0, parsed)
  if (parsed <= 100) return Math.max(0, Math.min(1, parsed / 100))
  return null
}

function resolveReceiptConfidence(receipt, pipeline) {
  const candidates = [
    receipt?.confidence,
    pipeline?.confidence,
    receipt?.parsed_json?.receipt?.confidence,
    receipt?.ocr_raw_json?.imagePipeline?.confidence,
    receipt?.ocr_raw_json?.qrPipeline?.confidence,
  ]
  for (const candidate of candidates) {
    const normalized = parseConfidenceCandidate(candidate)
    if (normalized != null) return normalized
  }
  return null
}

function base64SizeBytes(rawBase64) {
  if (typeof rawBase64 !== 'string') return null
  const payload = rawBase64.includes(',') ? rawBase64.split(',')[1] : rawBase64
  const normalized = payload.replace(/\s/g, '')
  if (!normalized) return null
  const padding = normalized.endsWith('==') ? 2 : normalized.endsWith('=') ? 1 : 0
  const sizeBytes = Math.floor((normalized.length * 3) / 4) - padding
  return Number.isFinite(sizeBytes) && sizeBytes >= 0 ? sizeBytes : null
}

function buildImageMetadata(sourceData, fetchedMimeType) {
  const normalizedSourceData = sourceData && typeof sourceData === 'object' ? sourceData : {}
  const typedSourceData = normalizedSourceData
  const fromSourceMime = (
    typedSourceData.mime_type
    ?? typedSourceData.mimeType
    ?? typedSourceData.content_type
    ?? typedSourceData.contentType
    ?? typedSourceData.type
  )
  const fromDataUrlMime = typeof typedSourceData.base64 === 'string'
    ? typedSourceData.base64.match(/^data:([^;]+);base64,/)?.[1] ?? null
    : null
  const sizeBytes = typedSourceData.size_bytes
    ?? typedSourceData.file_size
    ?? typedSourceData.size
    ?? base64SizeBytes(typedSourceData.base64)
    ?? null
  const fileName = (
    typedSourceData.file_name
    ?? typedSourceData.filename
    ?? typedSourceData.name
    ?? typedSourceData.original_name
    ?? typedSourceData.originalName
    ?? null
  )

  const metadata = {
    source: 'image',
    file_name: fileName,
    content_type: fetchedMimeType || fromSourceMime || fromDataUrlMime || null,
    size_bytes: sizeBytes != null && Number.isFinite(Number(sizeBytes)) ? Number(sizeBytes) : null,
    image_url: typeof typedSourceData.url === 'string' ? typedSourceData.url : null,
  }

  return Object.fromEntries(Object.entries(metadata).filter(([, value]) => value != null && value !== ''))
}

export default function ReceiptDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const [receipt, setReceipt] = useState(null)
  const [loading, setLoading] = useState(true)
  const pollingRef = useRef(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('parsed')
  const [retrying, setRetrying] = useState(false)
  const [retryMessage, setRetryMessage] = useState(null)
  const [receiptImageSrc, setReceiptImageSrc] = useState(null)
  const [receiptImageContentType, setReceiptImageContentType] = useState(null)
  const [receiptImageLoading, setReceiptImageLoading] = useState(false)
  const [receiptImageError, setReceiptImageError] = useState(null)
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === '1')
  const [savingEdit, setSavingEdit] = useState(false)
  const [editError, setEditError] = useState('')
  const [editForm, setEditForm] = useState({
    status: '',
    notes: '',
    sellerName: '',
    receiptDate: '',
    amount: '',
    currency: '',
    receiptCategoryIds: [],
    products: [],
  })
  const [initialProductIds, setInitialProductIds] = useState([])
  const [productCategories, setProductCategories] = useState([])
  const [receiptCategories, setReceiptCategories] = useState([])
  const [imageAssetsData, setImageAssetsData] = useState({ assets: [], stages: [] })
  const { currencies: CURRENCY_LIST } = useCurrencyCatalog()

  function normalizeStatus(value) {
    const raw = String(value ?? '').trim().toLowerCase()
    if (!raw || raw === 'null') return 'pending'
    return raw
  }

  const ocrStatus = normalizeStatus(receipt?.ocr_status)
  const productAnalysisStatus = normalizeStatus(receipt?.product_analysis_status)
  const productTranslationStatus = normalizeStatus(receipt?.product_translation_status)
  const qrExtractionStatus = normalizeStatus(receipt?.qr_code_product_extraction_status)

  function resolveReceiptCurrencyValue(currentReceipt) {
    const candidate = currentReceipt?.currency
      ?? currentReceipt?.summary?.currency
      ?? currentReceipt?.parsed_json?.receipt?.summary?.currency
      ?? currentReceipt?.parsed_json?.summary?.currency
      ?? currentReceipt?.parsed_json?.receipt?.currency
      ?? currentReceipt?.parsed_json?.currency
      ?? ''
    const normalized = String(candidate ?? '').trim().toUpperCase()
    if (normalized) return normalized
    return CURRENCY_LIST[0] ?? 'EUR'
  }

  function formatReceiptMoney(amount, currency) {
    if (amount == null || amount === '') return formatEmptyValue(null)
    return formatCurrency(amount, currency)
  }

  useEffect(() => {
    fetchReceipt()
  }, [id])

  function isPollingNeeded(r) {
    if (!r?.id) return false
    const st = normalizeSourceType(r)
    const statuses = st === 'qrcode'
      ? [normalizeStatus(r.qr_code_product_extraction_status), normalizeStatus(r.product_translation_status)]
      : st === 'speech'
        ? [normalizeStatus(r.ocr_status)]
        : [normalizeStatus(r.ocr_status), normalizeStatus(r.product_analysis_status), normalizeStatus(r.product_translation_status)]
    return statuses.some((s) => s === 'pending' || s === 'processing')
  }

  const pollingEnabled = Boolean(receipt?.id) && isPollingNeeded(receipt) && !isEditing
  usePolling(async () => {
    try {
      pollingRef.current = true
      const res = await apiClient.get(`/receipts/${id}`)
      const loaded = res.data?.data ?? res.data
      setReceipt(loaded)
    } catch {
      // Keep showing last known state on poll errors.
    } finally {
      pollingRef.current = false
    }
  }, {
    enabled: pollingEnabled,
    intervalMs: 30000,
    immediate: false,
  })

  useEffect(() => {
    fetchEditDictionaries()
  }, [])

  useEffect(() => {
    setIsEditing(searchParams.get('edit') === '1')
  }, [searchParams])

  useEffect(() => {
    const sourceType = normalizeSourceType(receipt)
    if (!receipt?.id || sourceType !== 'image') {
      setReceiptImageSrc(null)
      setReceiptImageContentType(null)
      setReceiptImageLoading(false)
      setReceiptImageError(null)
      return
    }

    fetchReceiptImage(receipt.id)
  }, [receipt?.id, receipt?.source_type, receipt?.source_data])

  useEffect(() => {
    if (!receipt?.id) return
    receiptImageAssetsService.listByReceipt(receipt.id)
      .then((res) => setImageAssetsData(res.data?.data || { assets: [], stages: [] }))
      .catch(() => setImageAssetsData({ assets: [], stages: [] }))
  }, [receipt?.id])

  const fetchReceipt = async () => {
    try {
      if (!pollingRef.current) setLoading(true)
      setError('')
      const res = await apiClient.get(`/receipts/${id}`)
      const loaded = res.data?.data ?? res.data
      setReceipt(loaded)
      if (!pollingRef.current) initEditForm(loaded)
    } catch (err) {
      console.error('Failed to load receipt', err)
      if (!pollingRef.current) {
        setError(t('Unable to load receipt. Please go back and try again.', 'Unable to load receipt. Please go back and try again.'))
      }
    } finally {
      if (!pollingRef.current) setLoading(false)
    }
  }

  const fetchEditDictionaries = async () => {
    try {
      const productResponse = await apiClient.get('/categories', { params: { type: 'product' } })
      const productList = productResponse.data?.data ?? productResponse.data ?? []
      const normalizedProductList = Array.isArray(productList) ? productList : []
      setProductCategories(normalizedProductList)
      setReceiptCategories(normalizedProductList)
    } catch (err) {
      console.error('Failed to load category dictionaries for receipt editing', err)
    }
  }

  const getProductCategoryName = (product) => {
    if (product?.category?.name) return formatEmptyValue(product.category.name)
    const productCategoryId = getProductCategoryId(product)
    if (!productCategoryId) return formatEmptyValue(null)
    const match = productCategories.find((category) => category?.id === productCategoryId)
    return formatEmptyValue(match?.name)
  }

  const getProductCategoryId = (product) => {
    // WHY: QR/image/speech flows can provide either a flat category id or only a nested category object.
    const categoryId = product?.product_category_id ?? product?.category_id ?? product?.category?.id ?? ''
    return categoryId ? String(categoryId) : ''
  }

  const mergedReceiptCategoryOptions = (() => {
    const map = new Map()
    for (const category of receiptCategories) {
      if (!category?.id) continue
      map.set(category.id, category.name ?? String(category.id))
    }
    const fromReceiptObjects = Array.isArray(receipt?.categories) ? receipt.categories : []
    for (const category of fromReceiptObjects) {
      if (!category?.id) continue
      map.set(category.id, category.name ?? String(category.id))
    }
    const selectedIds = Array.isArray(editForm.receiptCategoryIds) ? editForm.receiptCategoryIds : []
    for (const categoryId of selectedIds) {
      if (!categoryId || map.has(categoryId)) continue
      map.set(categoryId, categoryId)
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  })()

  const mergedProductCategoryOptions = (() => {
    const map = new Map()
    for (const category of productCategories) {
      if (!category?.id) continue
      map.set(category.id, category.name ?? String(category.id))
    }
    const products = Array.isArray(receipt?.products) ? receipt.products : []
    for (const product of products) {
      const id = getProductCategoryId(product)
      if (!id) continue
      const name = product?.category?.name ?? id
      map.set(id, name)
    }
    const editingProducts = Array.isArray(editForm.products) ? editForm.products : []
    for (const product of editingProducts) {
      const id = product?.product_category_id
      if (!id || map.has(id)) continue
      map.set(id, id)
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  })()

  const getReceiptCategoryLabels = () => {
    const byObject = Array.isArray(receipt?.categories) ? receipt.categories.map((category) => category?.name).filter(Boolean) : []
    if (byObject.length > 0) return byObject
    return []
  }

  const initEditForm = (loadedReceipt) => {
    if (!loadedReceipt) return
    const parsedReceipt = loadedReceipt.parsed_json?.receipt ?? {}
    setEditForm({
      status: loadedReceipt.status ?? '',
      notes: loadedReceipt.notes ?? '',
      sellerName: parsedReceipt.seller_name ?? parsedReceipt.vendor ?? parsedReceipt.merchant ?? '',
      receiptDate: parsedReceipt.date ?? '',
      amount: parsedReceipt.total ?? parsedReceipt.amount ?? '',
      currency: resolveReceiptCurrencyValue(loadedReceipt),
      receiptCategoryIds: Array.isArray(loadedReceipt.categories)
        ? loadedReceipt.categories.map((category) => category?.id).filter(Boolean)
        : [],
      products: Array.isArray(loadedReceipt.products)
        ? loadedReceipt.products.map((product) => ({
          id: product.id ?? null,
          name: product.name ?? '',
          translated_name: product.translated_name ?? '',
          qty: product.qty ?? '',
          price: product.price ?? '',
          product_category_id: getProductCategoryId(product),
        }))
        : [],
    })
    setInitialProductIds(Array.isArray(loadedReceipt.products) ? loadedReceipt.products.map((product) => product.id).filter(Boolean) : [])
  }

  const setProductField = (index, key, value) => {
    setEditForm((prev) => ({
      ...prev,
      products: prev.products.map((product, i) => (i === index ? { ...product, [key]: value } : product)),
    }))
  }

  const addProductRow = () => {
    setEditForm((prev) => ({
      ...prev,
      products: [...prev.products, { id: null, name: '', translated_name: '', qty: '', price: '', product_category_id: '' }],
    }))
  }

  const removeProductRow = (index) => {
    setEditForm((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }))
  }

  const handleSaveEdit = async () => {
    if (!receipt) return
    try {
      setSavingEdit(true)
      setEditError('')
      const parsedReceipt = { ...(receipt.parsed_json?.receipt ?? {}) }
      // WHY: list fields (seller/date/amount) are resolved from parsed_json.receipt on the backend.
      parsedReceipt.seller_name = editForm.sellerName || null
      parsedReceipt.date = editForm.receiptDate || null
      parsedReceipt.total = editForm.amount === '' ? null : Number(editForm.amount)
      parsedReceipt.currency = editForm.currency || null
      const parsedSummary = { ...(parsedReceipt.summary ?? {}) }
      parsedSummary.currency = editForm.currency || null
      parsedReceipt.summary = parsedSummary

      await apiClient.put(`/receipts/${receipt.id}`, {
        status: editForm.status,
        currency: editForm.currency || null,
        notes: editForm.notes,
        parsedJson: { ...(receipt.parsed_json ?? {}), receipt: parsedReceipt },
        category_ids: editForm.receiptCategoryIds,
      })

      const keptProductIds = []
      for (const product of editForm.products) {
        const payload = {
          name: product.name || null,
          translated_name: product.translated_name || null,
          qty: product.qty === '' ? null : Number(product.qty),
          price: product.price === '' ? null : Number(product.price),
          // WHY: keep both key styles so all backend paths map product category updates.
          productCategoryId: product.product_category_id || null,
          product_category_id: product.product_category_id || null,
        }
        if (product.id) {
          keptProductIds.push(product.id)
          await apiClient.put(`/receipt-products/${product.id}`, payload)
        } else if (product.name?.trim()) {
          await apiClient.post(`/receipts/${receipt.id}/products`, payload)
        }
      }

      const idsToDelete = initialProductIds.filter((productId) => !keptProductIds.includes(productId))
      for (const productId of idsToDelete) {
        await apiClient.delete(`/receipt-products/${productId}`)
      }

      await fetchReceipt()
      setIsEditing(false)
      setSearchParams({})
    } catch (err) {
      console.error('Failed to save receipt changes', err)
      setEditError(t('Failed to save receipt changes.', 'Failed to save receipt changes.'))
    } finally {
      setSavingEdit(false)
    }
  }

  const arrayBufferToBase64 = (buffer) => {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }

  const fetchReceiptImage = async (receiptId) => {
    setReceiptImageLoading(true)
    setReceiptImageError(null)
    try {
      const response = await apiClient.get(`/receipts/${receiptId}/image`, { responseType: 'arraybuffer' })
      const contentType = response?.headers?.['content-type'] || 'image/jpeg'
      const base64Data = arrayBufferToBase64(response.data)
      setReceiptImageContentType(contentType)
      setReceiptImageSrc(`data:${contentType};base64,${base64Data}`)
    } catch (err) {
      console.error('Failed to load receipt image', err)
      setReceiptImageSrc(null)
      setReceiptImageContentType(null)
      setReceiptImageError(t('Unable to load receipt image.', 'Unable to load receipt image.'))
    } finally {
      setReceiptImageLoading(false)
    }
  }

  // Trigger reprocessing of this receipt
  const handleRetry = async () => {
    if (!receipt) return
    try {
      setRetrying(true)
      setRetryMessage(null)
      await apiClient.post(`/receipts/${receipt.id}/reprocess`, {
        strategy: resolveReprocessStrategy(receipt),
      })
      setRetryMessage({ type: 'success', text: t('Receipt queued for reprocessing.', 'Receipt queued for reprocessing.') })
      // Refresh receipt data after a short delay to pick up new status
      setTimeout(() => fetchReceipt(), 2000)
    } catch (err) {
      console.error('Retry failed', err)
      setRetryMessage({ type: 'error', text: t('Failed to queue receipt for reprocessing.', 'Failed to queue receipt for reprocessing.') })
    } finally {
      setRetrying(false)
    }
  }

  // Helper: render a key-value data block as a list
  const renderDataList = (obj) => {
    if (!obj || typeof obj !== 'object') return <span className="text-muted-foreground">{formatEmptyValue(null)}</span>
    const entries = Object.entries(obj)
    if (entries.length === 0) return <span className="text-muted-foreground">Empty</span>
    return (
      <dl className="divide-y divide-slate-100">
        {entries.map(([key, val]) => (
          <div key={key} className="grid grid-cols-3 gap-2 py-1">
            <dt className="col-span-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">{key}</dt>
            <dd className="col-span-2 text-sm text-foreground break-words">
              {typeof val === 'object' ? <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(val, null, 2)}</pre> : formatEmptyValue(val)}
            </dd>
          </div>
        ))}
      </dl>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className={ui.spinner} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/receipts')}>{'\u2190'} {t('Back to Receipts', 'Back to Receipts')}</Button>
        <div className={ui.alertError}>{error}</div>
      </div>
    )
  }

  const pipeline = receipt?.ocr_raw_json?.qrPipeline ?? receipt?.ocr_raw_json?.imagePipeline ?? null
  const strategy = pipeline?.strategy ?? null
  const confidence = resolveReceiptConfidence(receipt, pipeline)
  const logs = pipeline?.logs ?? []
  const parsedData = receipt?.parsed_json ?? null
  const rawText = receipt?.raw_text ?? null
  const rawHtml = pipeline?.rawHtml ?? receipt?.raw_html ?? null
  const sourceType = normalizeSourceType(receipt)
  const receiptCurrency = resolveReceiptCurrencyValue(receipt)
  const parsedReceiptSummary = receipt?.parsed_json?.receipt?.summary ?? {}
  const parsedReceiptData = receipt?.parsed_json?.receipt ?? {}
  const displayTotal = parsedReceiptSummary.total_amount ?? parsedReceiptData.total ?? null
  const displaySubtotal = parsedReceiptSummary.subtotal ?? null
  const displayTax = parsedReceiptSummary.tax_amount ?? null
  const imageMetadata = sourceType === 'image'
    ? buildImageMetadata(receipt?.source_data, receiptImageContentType)
    : null
  const assetById = new Map((imageAssetsData.assets || []).map((asset) => [asset.id, asset]))

  return (
    <PageShell>
      {/* Header */}
      <PageHeader
        title={`Receipt #${receipt.id}`}
        subtitle={`${receipt.created_at ? formatDateTime(receipt.created_at) : ''} · ${formatEmptyValue(receipt.source_type)}`}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/receipts')} className="text-sm text-blue-600 hover:underline">
              {'\u2190'} {t('Back to Receipts', 'Back to Receipts')}
            </button>
            <Button
              variant={isEditing ? 'secondary' : 'primary'}
              onClick={() => {
                if (isEditing) {
                  initEditForm(receipt)
                  setIsEditing(false)
                  setSearchParams({})
                  return
                }
                setIsEditing(true)
                setSearchParams({ edit: '1' })
              }}
            >
              {isEditing ? t('Cancel edit', 'Cancel edit') : t('Edit', 'Edit')}
            </Button>
            {isEditing && (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSaveEdit}
                disabled={savingEdit}
              >
                {savingEdit ? t('Saving...', 'Saving...') : t('Save', 'Save')}
              </Button>
            )}
            {/* retry button reuses Button component */}
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleRetry}
              disabled={retrying}
            >
              {retrying ? t('Queuing...', 'Queuing...') : t('Retry', 'Retry')}
            </Button>
          </div>
        }
      />

      {/* Retry feedback */}
      {retryMessage && (
        <div className={`rounded-lg border px-4 py-2 text-sm ${retryMessage.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {retryMessage.text}
        </div>
      )}
      {editError && <div className={ui.alertError}>{editError}</div>}

      {isEditing && (
        <Card>
          <h2 className="text-base font-semibold text-foreground mb-4">{t('Manual Receipt Edit', 'Manual Receipt Edit')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Status', 'Status')}</label>
              <input className={ui.input} value={editForm.status} onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Receipt date', 'Receipt date')}</label>
              <input className={ui.input} value={editForm.receiptDate} onChange={(e) => setEditForm((prev) => ({ ...prev, receiptDate: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Seller', 'Seller')}</label>
              <input className={ui.input} value={editForm.sellerName} onChange={(e) => setEditForm((prev) => ({ ...prev, sellerName: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Amount', 'Amount')}</label>
              <input className={ui.input} value={editForm.amount} onChange={(e) => setEditForm((prev) => ({ ...prev, amount: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Currency', 'Currency')}</label>
              <SearchableCurrencySelect
                id="receipt-detail-currency"
                value={editForm.currency}
                options={CURRENCY_LIST}
                onChange={(value) => setEditForm((prev) => ({ ...prev, currency: value }))}
                emptyLabel={t('Select currency', 'Select currency')}
                searchPlaceholder={t('Search currency', 'Search currency')}
                searchAriaLabel={t('Search currency', 'Search currency')}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Receipt categories', 'Receipt categories')}</label>
              <select
                multiple
                className={`${ui.select} min-h-[120px]`}
                value={editForm.receiptCategoryIds}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map((option) => option.value)
                  setEditForm((prev) => ({ ...prev, receiptCategoryIds: selected }))
                }}
              >
                {mergedReceiptCategoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Notes', 'Notes')}</label>
              <textarea className={`${ui.textarea} min-h-[88px]`} value={editForm.notes} onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))} />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-foreground">{t('Products', 'Products')}</h3>
              <Button size="sm" variant="secondary" onClick={addProductRow}>{t('Add product', 'Add product')}</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm divide-y divide-slate-200">
                <thead className="bg-surface-elevated text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left">{t('Name', 'Name')}</th>
                    <th className="px-3 py-2 text-left">{t('Translated name', 'Translated name')}</th>
                    <th className="px-3 py-2 text-left">{t('Category', 'Category')}</th>
                    <th className="px-3 py-2 text-right">{t('Qty', 'Qty')}</th>
                    <th className="px-3 py-2 text-right">{t('Price', 'Price')}</th>
                    <th className="px-3 py-2 text-right">{t('Actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {editForm.products.map((product, index) => (
                    <tr key={product.id ?? `new-${index}`}>
                      <td className="px-3 py-2"><input className={ui.input} value={product.name} onChange={(e) => setProductField(index, 'name', e.target.value)} /></td>
                      <td className="px-3 py-2"><input className={ui.input} value={product.translated_name} onChange={(e) => setProductField(index, 'translated_name', e.target.value)} /></td>
                      <td className="px-3 py-2">
                        <select className={ui.select} value={product.product_category_id} onChange={(e) => setProductField(index, 'product_category_id', e.target.value)}>
                          <option value="">{t('Select category', 'Select category')}</option>
                          {mergedProductCategoryOptions.map((category) => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2"><input className={ui.input} value={product.qty} onChange={(e) => setProductField(index, 'qty', e.target.value)} /></td>
                      <td className="px-3 py-2"><input className={ui.input} value={product.price} onChange={(e) => setProductField(index, 'price', e.target.value)} /></td>
                      <td className="px-3 py-2 text-right"><Button size="sm" variant="danger" onClick={() => removeProductRow(index)}>{t('Delete', 'Delete')}</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {/* Queue pipeline badges */}
      <Card>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">{t('Pipeline', 'Pipeline')}</p>
        <div className="flex flex-wrap gap-2">
          {sourceType === 'qrcode' ? (
            <>
              <StatusBadge label={t('QRcode', 'QRcode')} status={qrExtractionStatus} />
              <StatusBadge label={t('Categorization', 'Categorization')} status={productTranslationStatus} />
            </>
          ) : sourceType === 'speech' ? (
            <>
              <StatusBadge label={t('Speech', 'Speech')} status={ocrStatus} />
              <StatusBadge label={t('Categorization', 'Categorization')} status={productTranslationStatus} />
            </>
          ) : (
            <>
              <StatusBadge label={t('OCR', 'OCR')} status={ocrStatus} />
              <StatusBadge label={t('Products', 'Products')} status={productAnalysisStatus} />
              <StatusBadge label={t('Categorization', 'Categorization')} status={productTranslationStatus} />
            </>
          )}
        </div>
      </Card>

      {/* Quick-info row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="py-3 px-4 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('Status', 'Status')}</p>
          <p className="mt-1 text-sm font-semibold text-foreground">{formatEmptyValue(receipt.status)}</p>
        </Card>
        <Card className="py-3 px-4 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('Confidence', 'Confidence')}</p>
          <p className="mt-1 text-sm font-semibold text-foreground">
            {formatConfidence(confidence)}
          </p>
        </Card>
        <Card className="py-3 px-4 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('Strategy', 'Strategy')}</p>
          <p className="mt-1 text-sm font-semibold text-violet-700">{formatEmptyValue(strategy)}</p>
        </Card>
        <Card className="py-3 px-4 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('Currency', 'Currency')}</p>
          <p className="mt-1 text-sm font-semibold text-foreground">{receiptCurrency}</p>
        </Card>
        <Card className="py-3 px-4 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('Products', 'Products')}</p>
          <p className="mt-1 text-sm font-semibold text-foreground">{receipt.products?.length ?? 0}</p>
        </Card>
      </div>

      <Card>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">{t('Monetary Summary', 'Monetary Summary')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="text-sm text-foreground"><span className="font-semibold">{t('Total', 'Total')}:</span> {formatReceiptMoney(displayTotal, receiptCurrency)}</div>
          <div className="text-sm text-foreground"><span className="font-semibold">{t('Subtotal', 'Subtotal')}:</span> {formatReceiptMoney(displaySubtotal, receiptCurrency)}</div>
          <div className="text-sm text-foreground"><span className="font-semibold">{t('Tax', 'Tax')}:</span> {formatReceiptMoney(displayTax, receiptCurrency)}</div>
        </div>
      </Card>

      {getReceiptCategoryLabels().length > 0 && (
        <Card>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t('Receipt Categories', 'Receipt Categories')}</p>
          <div className="flex flex-wrap gap-2">
            {getReceiptCategoryLabels().map((name) => (
              <span
                key={name}
                className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700"
              >
                {name}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* QR link if present */}
      {receipt.qr_code_link && (
        <Card>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('QR Source URL', 'QR Source URL')}</p>
          <a href={receipt.qr_code_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-sm break-all">
            {receipt.qr_code_link}
          </a>
        </Card>
      )}

      {receipt.notes && (
        <Card>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Notes', 'Notes')}</p>
          <p className="text-sm text-foreground whitespace-pre-wrap break-words">{receipt.notes}</p>
        </Card>
      )}

      {sourceType === 'image' && (
        <Card>
          <h2 className="text-base font-semibold text-foreground mb-3">{t('Source Image', 'Source Image')}</h2>
          <div className="overflow-hidden rounded-lg border border-outline bg-surface-elevated min-h-[200px] flex items-center justify-center">
            {receiptImageLoading && (
              <p className="text-sm text-muted-foreground py-20">{t('Loading receipt image...', 'Loading receipt image...')}</p>
            )}
            {receiptImageError && !receiptImageLoading && (
              <p className="text-sm text-red-600 py-20">{receiptImageError}</p>
            )}
            {!receiptImageLoading && !receiptImageError && receiptImageSrc && (
              <img
                src={receiptImageSrc}
                alt="Receipt image"
                className="w-full max-h-96 object-contain"
              />
            )}
            {!receiptImageLoading && !receiptImageError && !receiptImageSrc && (
              <p className="text-sm text-muted-foreground py-20">{t('No receipt image available.', 'No receipt image available.')}</p>
            )}
          </div>
          <div className="mt-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t('Image Metadata', 'Image Metadata')}</p>
            {imageMetadata && Object.keys(imageMetadata).length > 0
              ? renderDataList(imageMetadata)
              : <p className="text-sm text-muted-foreground italic">{t('No image metadata available.', 'No image metadata available.')}</p>}
          </div>
        </Card>
      )}

      {/* Tab navigation */}
      <div className="border-b border-outline">
        <nav className="flex gap-6" aria-label="Receipt detail tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'parsed' && (
        <Card>
          <h2 className="text-base font-semibold text-foreground mb-4">{t('Parsed Receipt Data', 'Parsed Receipt Data')}</h2>
          {parsedData ? renderDataList(parsedData) : (
            <p className="text-sm text-muted-foreground italic">{t('No parsed data available yet.', 'No parsed data available yet.')}</p>
          )}
          {receipt.products && receipt.products.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-foreground mb-2">{t('Products', 'Products')} ({receipt.products.length})</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm divide-y divide-slate-200">
                  <thead className="bg-surface-elevated text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-2 text-left">{t('Name', 'Name')}</th>
                      <th className="px-4 py-2 text-left">{t('Category', 'Category')}</th>
                      <th className="px-4 py-2 text-right">{t('Qty', 'Qty')}</th>
                      <th className="px-4 py-2 text-right">{t('Price', 'Price')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {receipt.products.map((p, i) => (
                      <tr key={p.id ?? i}>
                        <td className="px-4 py-2 text-foreground">
                          {p.id ? (
                            <Link to={`/receipt-products?receipt_id=${receipt.id}`} className="text-blue-600 hover:underline">
                              {formatEmptyValue(p.name)}
                            </Link>
                          ) : (formatEmptyValue(p.name))}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{getProductCategoryName(p)}</td>
                        <td className="px-4 py-2 text-right text-muted-foreground">{formatEmptyValue(p.qty)}</td>
                        <td className="px-4 py-2 text-right text-muted-foreground">{formatReceiptMoney(p.price, receiptCurrency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'raw' && (
        <Card>
          <h2 className="text-base font-semibold text-foreground mb-4">{t('Raw Data', 'Raw Data')}</h2>
          {rawText ? (
            <pre className="whitespace-pre-wrap rounded-lg bg-surface-elevated p-4 text-xs text-foreground overflow-x-auto">
              {rawText}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground italic">{t('No raw text available.', 'No raw text available.')}</p>
          )}
          {rawHtml && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-foreground mb-2">{t('Raw HTML (from QR page)', 'Raw HTML (from QR page)')}</h3>
              <pre className="whitespace-pre-wrap rounded-lg bg-surface-elevated p-4 text-xs text-foreground overflow-x-auto max-h-96">
                {rawHtml}
              </pre>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'debug' && (
        <div className="space-y-4">
          <Card>
            <h2 className="text-base font-semibold text-foreground mb-4">{t('Pipeline Steps', 'Pipeline Steps')}</h2>
            <PipelineSteps logs={logs} strategy={strategy} />
          </Card>
          {pipeline && (
            <Card>
              <h2 className="text-base font-semibold text-foreground mb-4">{t('Pipeline Evaluation', 'Pipeline Evaluation')}</h2>
              {renderDataList(pipeline.evaluation ?? pipeline)}
            </Card>
          )}
          <Card>
            <h2 className="text-base font-semibold text-foreground mb-4">{t('Full Debug JSON', 'Full Debug JSON')}</h2>
            <pre className="whitespace-pre-wrap rounded-lg bg-surface-elevated p-4 text-xs text-foreground overflow-x-auto max-h-96">
              {JSON.stringify(receipt.ocr_raw_json ?? receipt.ocr_data ?? {}, null, 2)}
            </pre>
          </Card>
          <Card>
            <h2 className="text-base font-semibold text-foreground mb-4">{t('Image Stage Assets', 'Image Stage Assets')}</h2>
            {imageAssetsData.stages?.length ? (
              <div className="space-y-4">
                {imageAssetsData.stages.map((stage) => {
                  const output = stage.outputAssetId ? assetById.get(stage.outputAssetId) : null
                  return (
                    <div key={stage.id} className="rounded border border-outline p-3">
                      <div className="text-sm font-semibold text-foreground">{stage.step}</div>
                        <div className="text-xs text-muted-foreground mb-2">{stage.createdAt ? formatDateTime(stage.createdAt) : '-'}</div>
                      {output ? (
                        <img
                          src={receiptImageAssetsService.receiptAssetImageUrl(receipt.id, output.id)}
                          alt={stage.step}
                          className="w-full max-h-80 object-contain rounded border border-outline bg-surface-elevated"
                        />
                      ) : (
                        <div className="text-xs text-muted-foreground">No output asset</div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">{t('No image stage assets found.', 'No image stage assets found.')}</p>
            )}
          </Card>
        </div>
      )}
    </PageShell>
  )
}
